import { Helmet } from "react-helmet";
import BlogArticleLayout from "@/components/BlogArticleLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Phone, Bot, Zap, Settings, CheckCircle2, ArrowRight, Sparkles, Globe, Clock, Shield, Code, Terminal } from "lucide-react";
import { useTranslation } from "react-i18next";
import heroImage from "@/assets/blog/openclaw-phone-calls.jpg";

const OpenClawPhoneCalls = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('blog.openClawPhoneCalls.metaTitle')}</title>
        <meta name="description" content={t('blog.openClawPhoneCalls.metaDescription')} />
        <meta name="keywords" content="OpenClaw phone calls, OpenClaw Call24x7, AI agent phone integration, OpenClaw voice, autonomous AI phone, open source AI phone agent" />
        <link rel="canonical" href="https://call24x7.ai/blog/openclaw-phone-calls" />
        <meta name="robots" content="index, follow" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://call24x7.ai/blog/openclaw-phone-calls" />
        <meta property="og:title" content={t('blog.openClawPhoneCalls.title')} />
        <meta property="og:description" content={t('blog.openClawPhoneCalls.metaDescription')} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": t('blog.openClawPhoneCalls.title'),
            "description": t('blog.openClawPhoneCalls.metaDescription'),
            "author": { "@type": "Organization", "name": "Call24x7.AI" },
            "publisher": { "@type": "Organization", "name": "Call24x7.AI", "url": "https://call24x7.ai" },
            "datePublished": "2026-02-28",
            "dateModified": "2026-02-28"
          })}
        </script>
      </Helmet>

      <BlogArticleLayout
        title={t('blog.openClawPhoneCalls.title')}
        category={t('blog.categories.integration', 'Integration')}
        date="February 28, 2026"
        readTime={`8 ${t('blog.minRead')}`}
        heroImage={heroImage}
        heroAlt="OpenClaw AI Agent Phone Integration with Call24x7"
      >
        {/* Introduction highlight */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6 rounded-xl mb-8 border border-primary/20">
          <p className="text-lg font-medium text-foreground mb-0">
            <Sparkles className="inline w-5 h-5 mr-2 text-primary" />
            {t('blog.openClawPhoneCalls.highlight')}
          </p>
        </div>

        <p className="text-lg mb-6">{t('blog.openClawPhoneCalls.intro')}</p>

        {/* What is OpenClaw */}
        <h2 className="text-2xl font-bold mt-10 mb-4 flex items-center gap-2">
          <Bot className="w-6 h-6 text-primary" />
          {t('blog.openClawPhoneCalls.whatIsOpenClawTitle')}
        </h2>

        <p className="mb-4">{t('blog.openClawPhoneCalls.whatIsOpenClawDesc')}</p>

        {/* Stats */}
        <Card className="mb-8 border-primary/20">
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center p-4">
                <div className="text-3xl font-bold text-primary mb-1">100K+</div>
                <p className="text-sm text-muted-foreground">{t('blog.openClawPhoneCalls.stat1')}</p>
              </div>
              <div className="text-center p-4">
                <div className="text-3xl font-bold text-primary mb-1">MIT</div>
                <p className="text-sm text-muted-foreground">{t('blog.openClawPhoneCalls.stat2')}</p>
              </div>
              <div className="text-center p-4">
                <div className="text-3xl font-bold text-primary mb-1">24/7</div>
                <p className="text-sm text-muted-foreground">{t('blog.openClawPhoneCalls.stat3')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* The Problem */}
        <h2 className="text-2xl font-bold mt-10 mb-4 flex items-center gap-2">
          <Phone className="w-6 h-6 text-primary" />
          {t('blog.openClawPhoneCalls.problemTitle')}
        </h2>

        <p className="mb-6">{t('blog.openClawPhoneCalls.problemDesc')}</p>

        {/* The Solution */}
        <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-6 mb-8">
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            {t('blog.openClawPhoneCalls.solutionTitle')}
          </h3>
          <p className="text-muted-foreground">{t('blog.openClawPhoneCalls.solutionDesc')}</p>
        </div>

        {/* How to integrate - Steps */}
        <h2 className="text-2xl font-bold mt-10 mb-4">{t('blog.openClawPhoneCalls.howToTitle')}</h2>

        <p className="mb-6">{t('blog.openClawPhoneCalls.howToIntro')}</p>

        <div className="space-y-4 mb-8">
          {[1, 2, 3, 4].map((step) => (
            <Card key={step} className="border-l-4 border-l-primary">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">{step}</div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">{t(`blog.openClawPhoneCalls.step${step}Title`)}</h4>
                    <p className="text-muted-foreground">{t(`blog.openClawPhoneCalls.step${step}Desc`)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Code Example */}
        <h2 className="text-2xl font-bold mt-10 mb-4 flex items-center gap-2">
          <Terminal className="w-6 h-6 text-primary" />
          {t('blog.openClawPhoneCalls.codeExampleTitle')}
        </h2>

        <Card className="mb-8 bg-muted/30">
          <CardContent className="pt-6">
            <pre className="text-sm overflow-x-auto font-mono bg-background/80 p-4 rounded-lg border">
{`// OpenClaw skill: Call24x7 Phone Integration
{
  "name": "make_phone_call",
  "description": "Make or receive phone calls via Call24x7.AI",
  "parameters": {
    "phone_number": "+1-555-123-4567",
    "agent_prompt": "Schedule an appointment",
    "language": "en"
  }
}`}
            </pre>
            <p className="text-sm text-muted-foreground mt-3">{t('blog.openClawPhoneCalls.codeExampleDesc')}</p>
          </CardContent>
        </Card>

        {/* Use Cases */}
        <h2 className="text-2xl font-bold mt-10 mb-4">{t('blog.openClawPhoneCalls.useCasesTitle')}</h2>

        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {[
            { color: "blue", icon: <CheckCircle2 className="w-5 h-5 text-blue-600" /> },
            { color: "green", icon: <CheckCircle2 className="w-5 h-5 text-green-600" /> },
            { color: "purple", icon: <CheckCircle2 className="w-5 h-5 text-purple-600" /> },
            { color: "orange", icon: <CheckCircle2 className="w-5 h-5 text-orange-600" /> },
          ].map((item, idx) => (
            <Card key={idx} className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100/50 dark:from-${item.color}-950/30 dark:to-${item.color}-900/20 border-${item.color}-200/50`}>
              <CardContent className="pt-6">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  {item.icon}
                  {t(`blog.openClawPhoneCalls.useCase${idx + 1}Title`)}
                </h4>
                <p className="text-sm text-muted-foreground">{t(`blog.openClawPhoneCalls.useCase${idx + 1}Desc`)}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Benefits */}
        <h2 className="text-2xl font-bold mt-10 mb-4">{t('blog.openClawPhoneCalls.benefitsTitle')}</h2>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="flex items-start gap-3">
            <Clock className="w-6 h-6 text-primary shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold">{t('blog.openClawPhoneCalls.benefit1Title')}</h4>
              <p className="text-sm text-muted-foreground">{t('blog.openClawPhoneCalls.benefit1Desc')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Globe className="w-6 h-6 text-primary shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold">{t('blog.openClawPhoneCalls.benefit2Title')}</h4>
              <p className="text-sm text-muted-foreground">{t('blog.openClawPhoneCalls.benefit2Desc')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-primary shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold">{t('blog.openClawPhoneCalls.benefit3Title')}</h4>
              <p className="text-sm text-muted-foreground">{t('blog.openClawPhoneCalls.benefit3Desc')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Code className="w-6 h-6 text-primary shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold">{t('blog.openClawPhoneCalls.benefit4Title')}</h4>
              <p className="text-sm text-muted-foreground">{t('blog.openClawPhoneCalls.benefit4Desc')}</p>
            </div>
          </div>
        </div>

        {/* Example Conversation */}
        <h2 className="text-2xl font-bold mt-10 mb-4">{t('blog.openClawPhoneCalls.exampleTitle')}</h2>

        <Card className="mb-8 bg-muted/30">
          <CardContent className="pt-6">
            <div className="space-y-4 font-mono text-sm">
              <div className="flex gap-3">
                <span className="text-blue-500 font-semibold">{t('blog.openClawPhoneCalls.you')}:</span>
                <span>{t('blog.openClawPhoneCalls.exampleUser')}</span>
              </div>
              <div className="flex gap-3">
                <span className="text-green-500 font-semibold">OpenClaw:</span>
                <span>{t('blog.openClawPhoneCalls.exampleAI1')}</span>
              </div>
              <div className="flex gap-3 pl-6 border-l-2 border-primary/30 italic text-muted-foreground">
                <span>{t('blog.openClawPhoneCalls.exampleCalling')}</span>
              </div>
              <div className="flex gap-3">
                <span className="text-green-500 font-semibold">OpenClaw:</span>
                <span>{t('blog.openClawPhoneCalls.exampleAI2')}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground mb-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.openClawPhoneCalls.ctaTitle')}</h3>
            <p className="mb-4 opacity-90">{t('blog.openClawPhoneCalls.ctaDesc')}</p>
            <Button 
              variant="secondary" 
              size="lg"
              onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}
              className="gap-2"
            >
              {t('blog.openClawPhoneCalls.ctaButton')} <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>

        <p className="text-muted-foreground text-sm">
          <strong>{t('blog.openClawPhoneCalls.note')}:</strong> {t('blog.openClawPhoneCalls.noteDesc')}
        </p>
      </BlogArticleLayout>
    </>
  );
};

export default OpenClawPhoneCalls;
